/**
 * @file 支店選択モーダルコンポーネント
 * @description ユーザーが支店、インハウスを検索し、選択するためのモーダルを提供します。
 *
 * @property {boolean} open - モーダルが開いているかどうかを制御します。
 * @property {function} onClose - モーダルを閉じる際に呼び出されるコールバック関数です。
 * @property {function(string): void} onSelect - 支店が選択された際に呼び出されるコールバック関数です。選択された支店名を引数として受け取ります。
 */

import { useState, useMemo } from 'react';
import {
  Modal,
  Box,
  Typography,
  IconButton,
  Tabs,
  Tab,
  TextField,
  List,
  ListItemButton,
  ListItemText,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { allBranchesData } from '../../data/branchData';
import type { BranchCategory } from '../../data/branchData';

interface BranchSelectionModalProps {
  open: boolean;
  onClose: () => void;
  onSelect: (branch: string) => void;
}

export const BranchSelectionModal = ({
  open,
  onClose,
  onSelect,
}: BranchSelectionModalProps) => {
  const [activeTab, setActiveTab] = useState<BranchCategory>('branches');
  const [searchTerm, setSearchTerm] = useState('');

  /**
   * タブが変更されたときの処理
   */
  const handleTabChange = (
    _event: React.SyntheticEvent,
    newValue: BranchCategory,
  ) => {
    setActiveTab(newValue);
    setSearchTerm(''); // タブを切り替えたら検索語をリセット
  };

  /**
   * 検索キーワードが変更されたときの処理
   */
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  /**
   * 支店が選択されたときの処理
   */
  const handleBranchSelect = (branch: string) => {
    onSelect(branch);
    onClose();
  };

  /**
   * 表示する支店リストをメモ化
   */
  const filteredBranches = useMemo(() => {
    const lowercasedSearchTerm = searchTerm.toLowerCase();
    return allBranchesData[activeTab].filter((branch) =>
      branch.toLowerCase().includes(lowercasedSearchTerm),
    );
  }, [activeTab, searchTerm]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="branch-selection-modal-title"
      sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
    >
      <Box
        sx={{
          bgcolor: 'background.paper',
          borderRadius: 3,
          width: '95%',
          maxWidth: 500,
          height: '90vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* モーダルヘッダー */}
        <Box
          sx={{
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            p: '16px 20px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography
            variant="h6"
            component="h2"
            id="branch-selection-modal-title"
          >
            支店を選択
          </Typography>
          <IconButton onClick={onClose} sx={{ color: 'primary.contrastText' }}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* タブコンテナ */}
        <Box
          sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'grey.100' }}
        >
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="fullWidth"
            indicatorColor="primary"
            textColor="primary"
          >
            <Tab label="支店" value="branches" />
            <Tab label="インハウス" value="offices" />
          </Tabs>
        </Box>

        {/* 検索バー */}
        <Box sx={{ p: '15px 20px', borderBottom: 1, borderColor: 'divider' }}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="支店名で検索"
            value={searchTerm}
            onChange={handleSearchChange}
            size="small"
          />
        </Box>

        {/* 支店リスト */}
        <Box sx={{ flexGrow: 1, overflowY: 'auto' }}>
          <List disablePadding>
            {filteredBranches.length > 0 ? (
              filteredBranches.map((branch) => (
                <ListItemButton
                  key={branch}
                  onClick={() => handleBranchSelect(branch)}
                  divider
                >
                  <ListItemText primary={branch} />
                </ListItemButton>
              ))
            ) : (
              <ListItemText
                primary="該当する支店が見つかりません。"
                sx={{ textAlign: 'center', p: 2, color: 'text.secondary' }}
              />
            )}
          </List>
        </Box>
      </Box>
    </Modal>
  );
};
